import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import AirDatePicker from '../components/ui/AirDatePicker';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';

function formatDate(value) {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
}

function PurchaseReturnList({ onAddReturn, onEditReturn, onPreviewReturn, onDeleteReturn }) {
    const { hasPermission } = usePermissionContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [draftStartDate, setDraftStartDate] = useState('');
    const [draftEndDate, setDraftEndDate] = useState('');
    const [appliedStartDate, setAppliedStartDate] = useState('');
    const [appliedEndDate, setAppliedEndDate] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    const fetchReturns = async (sq='', cp=1, lim=10, filters={}) => {
        if (!sq) setLoading(true);
        try {
            const q = new URLSearchParams();
            if (sq) q.append('search', sq);
            q.append('page', cp);
            q.append('limit', lim);
            if (filters.startDate) q.append('startDate', filters.startDate);
            if (filters.endDate) q.append('endDate', filters.endDate);
            const result = await fetchApi(`/purchase-return?${q.toString()}`);
            if (result.purchaseReturns) {
                setRows(result.purchaseReturns);
                setTotalEntries(result.total || 0);
                setTotalPages(result.pages || 1);
            }
        } catch (error) {
            console.error(error);
            if (error.status !== 403) toast.error('Failed to load purchase returns');
        } finally { setLoading(false); }
    };

    useEffect(() => {
        const delay = setTimeout(() => fetchReturns(search, page, pageSize, { startDate: appliedStartDate, endDate: appliedEndDate }), 250);
        return () => clearTimeout(delay);
    }, [search, page, pageSize, appliedStartDate, appliedEndDate]);

    const requestSort = (key) => {
        setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending' }));
    };

    const onSubmitFilters = () => {
        let finalEndDate = draftEndDate;
        if (draftStartDate && !draftEndDate) {
            finalEndDate = new Date().toISOString().split('T')[0];
            setDraftEndDate(finalEndDate);
        }
        setAppliedStartDate(draftStartDate);
        setAppliedEndDate(finalEndDate);
        setPage(1);
    };

    const onResetFilters = () => {
        setDraftStartDate('');
        setDraftEndDate('');
        setAppliedStartDate('');
        setAppliedEndDate('');
        setSearch('');
        setPage(1);
    };

    const handleExcelDownload = () => {
        if (!rows || rows.length === 0) return toast.info("No data to export");
        const exportData = sortedRows.map((r, i) => ({
            'No.': i + 1,
            'Return No': r.return_no,
            'Purchase No': r.original_invoice_no || '',
            'Seller Name': r.seller_trade_name || r.seller_name || '',
            'Return Date': formatDate(r.return_date),
            'Amount': (parseFloat(r.total_amount) || 0).toFixed(2)
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(wb, ws, 'Purchase Returns');
        XLSX.writeFile(wb, 'Purchase_Returns.xlsx');
    };

    const handlePrint = () => {
        if (!rows || rows.length === 0) return toast.info("No data to print");
        const printWindow = window.open('', '', 'height=600,width=800');
        printWindow.document.write('<html><head><title>Purchase Returns</title>');
        printWindow.document.write('<style>body{font-family:Arial,sans-serif;} table{width:100%;border-collapse:collapse;} th,td{border:1px solid #ddd;padding:8px;text-align:left;} th{background-color:#f2f2f2;}</style>');
        printWindow.document.write('</head><body><h2>Purchase Returns</h2><table>');
        printWindow.document.write('<thead><tr><th>No.</th><th>Return No</th><th>Purchase No</th><th>Seller</th><th>Return Date</th><th>Amount</th></tr></thead><tbody>');
        sortedRows.forEach((r, i) => {
            printWindow.document.write(`<tr><td>${i+1}</td><td>${r.return_no||''}</td><td>${r.original_invoice_no||''}</td><td>${r.seller_trade_name||r.seller_name||''}</td><td>${formatDate(r.return_date)}</td><td>${(parseFloat(r.total_amount)||0).toFixed(2)}</td></tr>`);
        });
        printWindow.document.write('</tbody></table></body></html>');
        printWindow.document.close();
        printWindow.print();
    };

    const sortedRows = useMemo(() => {
        const items = [...rows];
        if (sortConfig.key) {
            items.sort((a, b) => {
                let av = a[sortConfig.key] ?? '', bv = b[sortConfig.key] ?? '';
                if (sortConfig.key === 'total_amount') { av = parseFloat(av)||0; bv = parseFloat(bv)||0; }
                else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
                if (av < bv) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (av > bv) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [rows, sortConfig]);

    const columns = [
        {
            key: 'seller_name',
            label: 'SELLER',
            sortable: true,
            render: (r) => (
                <>
                    <div style={{fontWeight:600}}>{r.seller_trade_name||r.seller_name}</div>
                    {r.seller_trade_name && r.seller_name !== r.seller_trade_name && <div style={{fontSize:'11px',color:'#666'}}>{r.seller_name}</div>}
                </>
            )
        },
        { key: 'return_no', label: 'RETURN NO', sortable: true, cellStyle: () => ({fontWeight:600,color:'#0c3447'}), render: (r) => r.return_no },
        { key: 'original_invoice_no', label: 'PURCHASE NO', sortable: true, render: (r) => r.original_invoice_no||'—' },
        { key: 'return_date', label: 'RETURN DATE', sortable: true, render: (r) => formatDate(r.return_date) },
        { key: 'total_amount', label: 'AMOUNT', sortable: true, cellStyle: () => ({fontWeight:600,textAlign:'center'}), render: (r) => `₹${(parseFloat(r.total_amount)||0).toFixed(2)}` },
        {
            key: 'preview',
            label: 'PREVIEW',
            cellStyle: () => ({ textAlign: 'center' }),
            render: (r) => (
                <button
                    type="button"
                    style={{
                        padding: '4px 12px',
                        backgroundColor: '#0c3447',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '12px'
                    }}
                    onClick={() => onPreviewReturn && onPreviewReturn(r)}
                >
                    Preview
                </button>
            )
        },
        {
            key: 'actions',
            label: 'ACTIONS',
            hidden: !(hasPermission('PurchaseReturn','update')||hasPermission('PurchaseReturn','delete')),
            render: (r) => (
                <div className="list-page-action-icons">
                    {hasPermission('PurchaseReturn','update') && (
                        <button type="button" className="list-page-icon-btn" title="Edit" onClick={() => onEditReturn && onEditReturn(r)} style={{marginRight:'8px'}}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                    )}
                    {hasPermission('PurchaseReturn','delete') && (
                        <button type="button" className="list-page-icon-btn" title="Delete" onClick={() => onDeleteReturn && onDeleteReturn(r._id||r.id)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                    )}
                </div>
            )
        }
    ];

    const footerRow = sortedRows.length > 0 ? (
        <tr style={{backgroundColor:'#eaeaea',fontWeight:'bold'}}>
            <td colSpan={4} style={{textAlign:'right',padding:'12px 10px',color:'#111827'}}>TOTAL</td>
            <td style={{fontWeight:'bold',padding:'12px 10px',textAlign:'center',color:'#111827'}}>₹{sortedRows.reduce((s,r)=>s+(parseFloat(r.total_amount)||0),0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
            <td></td>
            {(hasPermission('PurchaseReturn','update')||hasPermission('PurchaseReturn','delete')) && <td></td>}
        </tr>
    ) : null;

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">Purchase Return List</h1>
                <div className="list-page-action-buttons">
                    {hasPermission('PurchaseReturn','report') && (
                        <>
                            <button className="list-page-btn list-page-btn-excel" onClick={handleExcelDownload}>
                                EXCEL
                            </button>
                            <button className="list-page-btn list-page-btn-print" onClick={handlePrint}>
                                PRINT
                            </button>
                        </>
                    )}
                    {hasPermission('PurchaseReturn','add') && (
                        <button className="list-page-btn list-page-btn-add" onClick={() => onAddReturn && onAddReturn()}>ADD RETURN</button>
                    )}
                </div>
            </div>
            <DataTable
                columns={columns}
                data={sortedRows}
                sortConfig={sortConfig}
                onSort={requestSort}
                loading={loading}
                emptyMessage="No records found"
                page={page}
                pageSize={pageSize}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                footerRow={footerRow}
                searchBar={
                    <>
                        <SearchBar value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search return no, seller, invoice no..." />
                        <div className="list-page-filter-box">
                            <div className="list-page-filter-group">
                                <label className="list-page-filter-label">Start Date</label>
                                <AirDatePicker className="list-page-filter-date" value={draftStartDate} onChange={(val) => setDraftStartDate(val)} placeholder="Start Date" />
                            </div>
                            <div className="list-page-filter-group">
                                <label className="list-page-filter-label">End Date</label>
                                <AirDatePicker className="list-page-filter-date" value={draftEndDate} onChange={(val) => setDraftEndDate(val)} placeholder="End Date" />
                            </div>
                            <button className="list-page-btn list-page-btn-submit" onClick={onSubmitFilters}>Submit</button>
                            <button className="list-page-btn list-page-btn-reset" onClick={onResetFilters}>Reset</button>
                        </div>
                    </>
                }
            />
        </div>
    );
}

export default PurchaseReturnList;
